import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import Moment from 'moment';
import { FontIcon } from 'react-toolbox';
import MediaQuery from 'react-responsive';
import { isEmpty, uniq } from 'lodash';
import CalendarRow from 'calendarRow';
import CalendarDayCell from 'calendarDayCell';
import CalendarLegendCell from 'calendarLegendCell';
import CalendarAssigneeInfo from 'calendarAssigneeInfo';
import CalendarAppointment from 'calendarAppointment';
import CalendarBlock from 'calendarBlock';
import CalendarPTO from 'calendarPTO';
import CalendarTWD from 'calendarTWD';
import VendorBlock from './vendorBlock';
import { validateSource } from '../../../../../utils';
import { determineCellBlock, determineAdjustedStartTime,
         isTimeBlocked, isAvailableForDay } from '../../../../../utils/schedulingUtils';
import { AppointmentType } from 'types/appointment';
import ScheduleMemberType from 'types/scheduleMember';
import { CUT_POINT, DISPLAY_TIME } from 'config/constants';
import S from 'calendar.scss';

class CalendarAssigneeRow extends Component {
  state = { expanded: this.props.expand || true }

  componentWillReceiveProps = (nextProps) => {
    if (nextProps.expand !== this.props.expand) this.setState({ expanded: nextProps.expand });
  }

  onSelect = (date, adjustedStartTime, ssBlockage) => () =>
    this.props.onSelect && this.props.onSelect(
      date, this.props.member, adjustedStartTime, ssBlockage,
    );

  onOpeningClick = nextOpening => this.props.onOpeningClick &&
    this.props.onOpeningClick(nextOpening, this.props.member);

  getAllScheduled = (ssAvailability) => {
    if (isEmpty(ssAvailability)) return [];
    const { appts, pto } = ssAvailability;
    return appts.concat(pto);
  };

  getExistingAppts = (appts = []) => appts.filter(a => a.id > 0) || [];

  getAvailabilityArgs = (date, availability) => ({
    unavailableBefore: !isAvailableForDay(date.clone().subtract(1, 'day'), availability),
    unavailableAfter: !isAvailableForDay(date.clone().add(1, 'day'), availability),
  });

  getDailyAvailabilityFromAssignee = (assigneeAvailabilities) => {
    const allDays = [1, 2, 3, 4, 5, 6, 7];
    if (!assigneeAvailabilities.length) return ({ morning: allDays, afternoon: allDays });
    const dailyAvailabilities = assigneeAvailabilities.map(aA => aA.assignee_availability)
      .filter(aA => aA.store_id === this.props.pendingAppointment.phase.store.id)
      .map((aA) => {
        const { morning = [], afternoon = [], evening = [] } = aA;
        const availabilityArr = uniq(morning.concat(afternoon, evening)).map((i) => {
          switch (i) {
            case 'sunday':
              return 1;
            case 'monday':
              return 2;
            case 'tuesday':
              return 3;
            case 'wednesday':
              return 4;
            case 'thursday':
              return 5;
            case 'friday':
              return 6;
            case 'saturday':
              return 7;
            default:
              return 0;
          }
        });
        return ({ morning: availabilityArr, afternoon: availabilityArr });
      });
    return dailyAvailabilities.length ? dailyAvailabilities[0] : ({ morning: [], afternoon: [] });
  };

  expand = () => this.setState({ expanded: true });

  contract = () => this.setState({ expanded: false });

  apptsOnDay = (date, appts) => appts.filter(a =>
    Moment.range(Moment(a.start_date), Moment(a.end_date)).contains(date));

  ptoOnDay = (date, pto) => pto.find(p =>
    Moment.range(Moment(p.start_date), Moment(p.end_date)).contains(date));

  twdOnDay = (date, twd) => twd.find(t =>
    Moment.range(Moment(t.start_date), Moment(t.end_date)).contains(date));

  isTWDBlocked = (twd, phase, twdZip, coveredZips) => (!isEmpty(phase) &&
    ((!isEmpty(twd) && !twd.traveling_zip_codes.includes(phase.zip_code)) ||
    (isEmpty(twd) && twdZip && !coveredZips.includes(phase.zip_code))));

  isTWDZip = (phaseZip, travelZips) =>
    (travelZips.length < 1 ? false : travelZips.includes(phaseZip));

  matchesSelectedAssignee = () => {
    const { selectedAssignee, member: { assignee } } = this.props;
    return selectedAssignee && assignee.id === selectedAssignee.id &&
      assignee.kind === selectedAssignee.kind;
  };

  matchesSelectedDay = (date) => {
    const { selectedDayStart, selectedDayEnd, selectedAssignee } = this.props;
    return selectedAssignee && selectedDayStart && selectedDayEnd &&
      Moment.range(selectedDayStart, selectedDayEnd).contains(date);
  };

  userHasVendorPerms = (source) => {
    const { manageVendorWorkrooms: mvw } = this.props;
    return validateSource(source) ? mvw.lhi || false :
      mvw[source.toLowerCase()] || false;
  }

  isVendorBlocked = (appts) => {
    const { isScheduleBoard, phaseSource, phaseType } = this.props;
    const isEmployee = this.isUserEmployee();
    if (isScheduleBoard) {
      return isEmployee && (appts || []).some(a => !this.userHasVendorPerms(a.phase.source));
    }
    return isEmployee && (!this.userHasVendorPerms(phaseSource) ||
      (appts || []).some((a) => {
        const apptSource = a.phase.source;
        return !this.userHasVendorPerms(apptSource) || (phaseType !== 'Assessment' &&
          (validateSource(phaseSource) ?
            !validateSource(a.phase.source) : phaseSource !== a.phase.source));
      }));
  }

  filterApptsByVendor = appts => appts.filter(a =>
    !this.isUserEmployee() || this.userHasVendorPerms(a.phase.source));

  isSelected = date => this.matchesSelectedDay(date) && this.matchesSelectedAssignee();

  isCrew = kind => kind === 'Crew';

  isUserEmployee = () => this.props.employableType === 'Employee';

  renderDay = (date, appts, pto, applySsFeatures, twdData, allScheduled = []) => {
    const { expanded } = this.state;
    const { selectable, onReschedule, relatedPhases, pendingAppointment = {}, member: {
      assignee, projectedCompletion, projectedJobLength, projectedJobDays, setDelayedField,
      assignee: { default_start_time = '', covered_zip_codes = [], daily_availability = {
        morning: [], afternoon: [] }, kind = '', assignee_availabilities = [] },
    }, isCcs, isScheduleBoard, employableType } = this.props;
    let availabilityArgs = {};
    if (this.isCrew(kind)) {
      availabilityArgs = this.getAvailabilityArgs(date, daily_availability);
    } else if (kind === 'Estimator' && !isScheduleBoard) {
      const dailyAvailability = this.getDailyAvailabilityFromAssignee(assignee_availabilities);
      availabilityArgs = this.getAvailabilityArgs(date, dailyAvailability);
    }
    const { twd, twdZip } = twdData;
    const pendingAppt = { ...pendingAppointment, relatedPhases };
    const daySched = { appts, pto };
    let ssBlockage = '';
    let adjustedStartTime = default_start_time;
    if (applySsFeatures) {
      const crew = { ...assignee, projectedCompletion, projectedJobLength, projectedJobDays };
      ssBlockage = determineCellBlock(date, daySched, crew, pendingAppt, twdData, allScheduled, isCcs) || '';
      adjustedStartTime = (this.getExistingAppts(appts).length === 1) ?
      determineAdjustedStartTime(this.getExistingAppts(appts)[0], crew) : adjustedStartTime;
    }
    const vendorBlock = this.isVendorBlocked(appts.filter(a => a.id > 0));
    const disablePto = ['Crew', 'Estimator', 'Installer'].includes(employableType);
    return (<Fragment>
      <CalendarDayCell
        onSelect={(ssBlockage && isCcs) || vendorBlock ? () => {} :
          this.onSelect(date, adjustedStartTime, ssBlockage)}
        selectedRow={this.matchesSelectedAssignee()}
        selectedCol={this.matchesSelectedDay(date)}
        key={date}
        className={S.day}
        expanded={expanded}
        selectable={selectable}
      >
        {vendorBlock ? (<VendorBlock date={date} assignee={assignee} />) : (<Fragment>
          {ssBlockage && <CalendarBlock
            reason={ssBlockage}
            onSelect={this.onSelect(date, adjustedStartTime, ssBlockage)}
            adjustedStartTime={adjustedStartTime !== default_start_time &&
                              isTimeBlocked(ssBlockage) ?
              Moment(`${appts[0].start_date} ${adjustedStartTime}`).format(DISPLAY_TIME) : ''}
          />}
          {(pto || (!isEmpty(availabilityArgs) &&
          (!kind === 'Estimator' || !isScheduleBoard) &&
          !isAvailableForDay(date.clone(), this.isCrew(kind) ?
            daily_availability : this.getDailyAvailabilityFromAssignee(assignee_availabilities),
          ))) && <CalendarPTO
            pto={pto || null} date={date} assignee={assignee}
            availabilityArgs={!isEmpty(availabilityArgs) ? availabilityArgs : null}
            disablePto={disablePto}
          />}
          {this.isTWDBlocked(
            twd || {},
            pendingAppointment.phase || {},
            twdZip,
            covered_zip_codes,
          ) && <CalendarTWD twd={twd} date={date} />}
        </Fragment>)}
        <div className={S.appointmentsContainer}>
          <div className={S.appointments}>
            {this.filterApptsByVendor(appts).map((appointment, i) =>
              (expanded || i < 2 ? (<CalendarAppointment
                key={appointment.id}
                selectedCell={(appts || []).length > 1 && appts.some(a => a.id < 0)}
                appointment={appointment}
                date={date}
                highlight={appointment.selected}
                selectable={selectable}
                onReschedule={onReschedule}
                assignee={assignee}
                applySsFeatures={applySsFeatures}
                relatedPhases={relatedPhases}
                pendingId={(pendingAppointment.phase || {}).id}
                setDelayedField={setDelayedField}
              />) : null))}
          </div>
          {appts.length > 2 && !expanded && <div className={S.dayTotal}>
            {appts.length}
          </div>}
        </div>
      </CalendarDayCell>
      {selectable && !vendorBlock && !(ssBlockage && isCcs) && <MediaQuery maxWidth={CUT_POINT - 1}>
        {isMobile => (isMobile ? (<button
          onClick={this.onSelect(date, adjustedStartTime, ssBlockage)}
          className={S.expand}
        >
          <span className={S.label}>Select Day</span>
          <FontIcon value="add" />
        </button>) : null)}
      </MediaQuery>}
    </Fragment>);
  }

  render = () => {
    const {
      dates, expand, pendingAppointment = {}, role, phaseZip,
      applySmartScheduling, isScheduleBoard, selectedAssignee = null,
      phaseInstallerId, phaseEstimatorId,
      member: {
        assignee: { id, kind, specificMismatch = false, traveling_zip_codes = [], installer_id },
        assignee, pto = [], twd = [], nextOpening = '', appointments = [], ss_availability = {},
        projectedJobDays = 0, projectedJobLength = 0, projectedCompletion = '',
      },
    } = this.props;
    const { expanded } = this.state;
    let appts = appointments.filter(a => !pendingAppointment || a.id !== pendingAppointment.id);
    if (this.matchesSelectedAssignee()) appts = [...appts, { ...pendingAppointment }];
    const apptsByDay = dates.map(date => this.apptsOnDay(date, appts));
    const ptoByDay = dates.map(date => this.ptoOnDay(date, pto));
    const twdByDay = dates.map(date => this.twdOnDay(date, twd));
    const expandable = apptsByDay.some(day => day.length > 2) && !expand;
    const isIfi = this.props.phaseSource === 'IFI';
    const isPreferredCrew = pendingAppointment && applySmartScheduling &&
      this.isCrew(kind) && !specificMismatch;
    const applySsFeatures = !isScheduleBoard && isPreferredCrew && !isIfi;
    // For IFI, the green "preferred" row means the phase's assigned installer's crew
    // (or assigned estimator) — the rows shown by default. Revealed "mismatched" rows stay white.
    const ifiMatchedRow = isIfi && !isScheduleBoard && (
      (this.isCrew(kind) && !!phaseInstallerId && installer_id === phaseInstallerId) ||
      (kind === 'Estimator' && !!phaseEstimatorId && id === phaseEstimatorId)
    );
    const highlightRow = isIfi ? ifiMatchedRow : (isPreferredCrew && applySmartScheduling);
    const allScheduled = (applySsFeatures ?
      this.getAllScheduled(ss_availability) :
      appts.concat(pto)) || [];
    const twdZip = this.isTWDZip((pendingAppointment.phase || {}).zip_code || '', traveling_zip_codes || []);
    return (<CalendarRow
      key={`${kind + id}`}
      className={highlightRow ? S.crewSpecificMatchRow : S.memberRow}
    >
      <CalendarLegendCell className={S.assignee}>
        <CalendarAssigneeInfo
          assignee={assignee}
          selectedAssignee={selectedAssignee}
          role={role}
          showProjectedTime={applySsFeatures}
          showIfTraveling={!isScheduleBoard && this.isCrew(kind)}
          projectedJobDays={projectedJobDays || 0}
          projectedJobLength={Math.round((projectedJobLength || 0) * 100) / 100}
          projectedCompletion={projectedCompletion}
          nextOpening={nextOpening || ''}
          onOpeningClick={() => this.onOpeningClick(nextOpening || '')}
          phaseZip={phaseZip}
        />
      </CalendarLegendCell>
      {dates.map((date, i) => this.renderDay(date, apptsByDay[i], ptoByDay[i], applySsFeatures, {
        twd: twdByDay[i], twdZip,
      }, allScheduled))}
      {expandable && !expanded && <button onClick={this.expand} className={S.expand}>
        <span className={S.label}>Expand Row</span>
        <FontIcon value="arrow_drop_down" />
      </button>}
      {expandable && expanded && <button onClick={this.contract} className={S.contract}>
        <span className={S.label}>Close</span>
        <FontIcon value="arrow_drop_up" />
      </button>}
    </CalendarRow>);
  }
}

CalendarAssigneeRow.propTypes = {
  dates: PropTypes.arrayOf(PropTypes.instanceOf(Moment)).isRequired,
  member: ScheduleMemberType.isRequired,
  expand: PropTypes.bool,
  onSelect: PropTypes.func,
  selectedDayStart: PropTypes.instanceOf(Moment),
  selectedDayEnd: PropTypes.instanceOf(Moment),
  selectedAssignee: PropTypes.shape({
    id: PropTypes.number,
    kind: PropTypes.string,
  }),
  pendingAppointment: AppointmentType,
  selectable: PropTypes.bool,
  onReschedule: PropTypes.func,
  role: PropTypes.string,
  isScheduleBoard: PropTypes.bool,
  applySmartScheduling: PropTypes.bool,
  onOpeningClick: PropTypes.func,
  relatedPhases: PropTypes.array,
  setDelayedField: PropTypes.func,
  isCcs: PropTypes.bool,
  manageVendorWorkrooms: PropTypes.object,
  employableType: PropTypes.string,
  phaseSource: PropTypes.string,
  phaseZip: PropTypes.string,
  phaseType: PropTypes.string,
  phaseInstallerId: PropTypes.number,
  phaseEstimatorId: PropTypes.number,
};

export default CalendarAssigneeRow;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/schedule/components/calendarAssigneeRow.jsx